/**
 * The deals on one workflow, each with its latest memo run.
 *
 * Hold-and-dim, like the run hook: a refresh keeps the list on screen and
 * marks it `stale` rather than dropping to a skeleton, because most refetches
 * move one row's pill.
 *
 * Polled every 5 s ONLY while some deal's latest run is generating — that is
 * the one state the list changes in without the user doing anything, and a
 * list nobody is waiting on should put nothing on the wire. A poll never sets
 * `stale`: dimming the list every 5 s for the length of a request was a
 * flicker, and the rows on screen are still the right answer until the
 * response says otherwise.
 *
 * `addDeal` treats the POST's answer as the result: a read that fails
 * afterwards is not a failed add (a retry would create a second deal), so the
 * created row is kept and returned, and the failed re-read shows up as the
 * list's own `error`, the same as any other refresh that failed.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { dealScreeningService } from "../services/dealScreeningService";
import type {
	DealScreeningCreateDealPayload,
	DealScreeningDeal,
	DealScreeningRunRequest,
} from "../types/dealScreening";
import { DEAL_SCREENING_POLL_MS } from "./useDealScreeningRun";
import { isRateLimited } from "../utils/isRateLimited";

/** Why a read runs: the first paint, a refresh someone asked for, or the background poll. */
type LoadMode = "initial" | "refresh" | "poll";

export interface DealScreeningDealsData {
	deals: DealScreeningDeal[];
	loading: boolean;
	error: Error | undefined;
	stale: boolean;
	refresh: () => Promise<void>;
	addDeal: (payload: DealScreeningCreateDealPayload) => Promise<DealScreeningDeal>;
	renameDeal: (dealId: string, name: string) => Promise<DealScreeningDeal>;
	/** Which deal-specific sections this deal's next memo leaves out. */
	setDealSections: (dealId: string, excluded: string[]) => Promise<DealScreeningDeal>;
	removeDeal: (dealId: string) => Promise<void>;
	runDeal: (dealId: string) => Promise<DealScreeningRunRequest>;
}

export const anyGenerating = (deals: DealScreeningDeal[]): boolean =>
	deals.some((deal) => deal.latest_run?.generating === true);

export function useDealScreeningDeals(
	workflowId: string | undefined,
): DealScreeningDealsData {
	const [deals, setDeals] = useState<DealScreeningDeal[]>([]);
	const [loading, setLoading] = useState(true);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	// A 429 stops the poll. Polling into a spent budget only keeps it spent;
	// the next read that succeeds (a manual refresh, a write's re-read) resumes it.
	const [rateLimited, setRateLimited] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const requestId = useRef(0);

	const load = useCallback(
		async (mode: LoadMode) => {
			const initial = mode === "initial";
			if (!workflowId) return;
			const id = ++requestId.current;
			if (initial) setLoading(true);
			// A poll is silent: the list changes without the user doing anything,
			// so dimming it every 5 s read as a flicker, not as feedback.
			else if (mode === "refresh") setStale(true);
			try {
				const next = await dealScreeningService.listDeals(workflowId);
				if (!mounted.current || id !== requestId.current) return;
				setDeals(next);
				setError(undefined);
				setRateLimited(false);
			} catch (caught) {
				if (!mounted.current || id !== requestId.current) return;
				const failure = caught instanceof Error ? caught : new Error(String(caught));
				setError(failure);
				if (isRateLimited(failure)) setRateLimited(true);
				if (initial) setDeals([]);
			} finally {
				if (mounted.current && id === requestId.current) {
					setLoading(false);
					setStale(false);
				}
			}
		},
		[workflowId],
	);

	useEffect(() => {
		setDeals([]);
		setError(undefined);
		setLoading(true);
		void load("initial");
	}, [load]);

	const refresh = useCallback(() => load("refresh"), [load]);

	const generating = anyGenerating(deals);
	useEffect(() => {
		if (!generating || !workflowId || rateLimited) return;
		const timer = window.setInterval(() => {
			void load("poll");
		}, DEAL_SCREENING_POLL_MS);
		return () => window.clearInterval(timer);
	}, [generating, workflowId, rateLimited, load]);

	const addDeal = useCallback(
		async (payload: DealScreeningCreateDealPayload) => {
			if (!workflowId) throw new Error("No workflow was named in the URL.");
			const created = await dealScreeningService.createDeal(workflowId, payload);
			// The re-read is housekeeping, and must never turn a deal that exists
			// into "Could not add the deal". `load` records its own failure as
			// `error`; anything that still escapes it is swallowed HERE, so the
			// guarantee does not depend on how `load` handles errors.
			try {
				await load("refresh");
			} catch {
				// Reported through `error` by `load`; the add itself succeeded.
			}
			if (mounted.current) {
				setDeals((current) =>
					current.some((deal) => deal.public_id === created.public_id)
						? current
						: [...current, created],
				);
			}
			return created;
		},
		[workflowId, load],
	);

	/** A new label for a deal; the folder it reads is unchanged. */
	const renameDeal = useCallback(async (dealId: string, name: string) => {
		const updated = await dealScreeningService.updateDeal(dealId, { name });
		// A list read already in flight answers for the world before the
		// rename; retiring its request id keeps it from putting the old name
		// back. The next poll or refresh reads the renamed row.
		requestId.current += 1;
		if (mounted.current) {
			setDeals((current) =>
				current.map((deal) => (deal.public_id === updated.public_id ? updated : deal)),
			);
		}
		return updated;
	}, []);

	const setDealSections = useCallback(async (dealId: string, excluded: string[]) => {
		const updated = await dealScreeningService.updateDeal(dealId, {
			excluded_section_keys: excluded,
		});
		requestId.current += 1; // as in renameDeal
		if (mounted.current) {
			setDeals((current) =>
				current.map((deal) => (deal.public_id === updated.public_id ? updated : deal)),
			);
		}
		return updated;
	}, []);

	/** The deal and its memos go; the Deal Room folder stays. */
	const removeDeal = useCallback(async (dealId: string) => {
		await dealScreeningService.deleteDeal(dealId);
		requestId.current += 1; // as in renameDeal: a stale read must not resurrect it
		if (mounted.current) {
			setDeals((current) => current.filter((deal) => deal.public_id !== dealId));
		}
	}, []);

	const runDeal = useCallback(
		async (dealId: string) => {
			const result = await dealScreeningService.runDeal(dealId);
			// The row shows the run the server just named straight away, so the
			// poll (which keys off `generating`) starts without waiting a read.
			if (mounted.current) {
				setDeals((current) =>
					current.map((deal) =>
						deal.public_id === dealId ? { ...deal, latest_run: result.run } : deal,
					),
				);
			}
			return result;
		},
		[],
	);

	return {
		deals,
		loading,
		error,
		stale,
		refresh,
		addDeal,
		renameDeal,
		setDealSections,
		removeDeal,
		runDeal,
	};
}
